import type {
  AiAllocationAction,
  AiAllocationResponse,
  AiDisambiguation,
  AiDisambiguationOption,
} from '../types/teacherAllocationAi'
import {buildAllocationSlotKey} from './allocationIds'

const isCompositeSlotKey = (value: unknown): boolean => {
  const s = String(value ?? '').trim()
  return s.length > 0 && s.includes('|||')
}

const resolveActionSlotKey = (raw: any): string => {
  const explicit = String(raw?.slotKey ?? raw?.slot_key ?? '').trim()
  if (explicit) return explicit

  const subjectId = raw?.subjectId ?? raw?.subject_id
  if (isCompositeSlotKey(subjectId)) return String(subjectId).trim()

  const classKey = String(raw?.classKey ?? raw?.class_key ?? '').trim()
  const subjectName = String(raw?.subjectName ?? raw?.subject_name ?? '').trim()
  if (classKey && subjectName) return buildAllocationSlotKey(classKey, subjectName)

  return ''
}

const resolveTeacherEmployeeId = (raw: any): string | null => {
  const explicit = raw?.teacherEmployeeId ?? raw?.teacher_employee_id
  if (explicit !== null && explicit !== undefined) {
    const value = String(explicit).trim()
    if (value) return value
  }

  const teacherId = raw?.teacherId ?? raw?.teacher_id
  if (teacherId === null || teacherId === undefined) return null
  const value = String(teacherId).trim()
  if (!value) return null
  if (/^\d+$/.test(value)) return null
  return value
}

const normalizeAction = (raw: any): AiAllocationAction | null => {
  const slotKey = resolveActionSlotKey(raw)
  if (!slotKey) return null

  const rawType = String(raw?.type ?? 'assign').toLowerCase()
  const type: AiAllocationAction['type'] =
    rawType === 'unassign' || rawType === 'clear'
      ? 'unassign'
      : rawType === 'replace' || rawType === 'overwrite' || rawType === 'reassign'
        ? 'replace'
        : 'assign'

  const teacherNameRaw = raw?.teacherName ?? raw?.teacher_name
  const teacherName =
    teacherNameRaw === null || teacherNameRaw === undefined
      ? null
      : String(teacherNameRaw).trim() || null

  return {
    type,
    slotKey,
    teacherEmployeeId: resolveTeacherEmployeeId(raw),
    teacherName,
  }
}

const normalizeDisambiguationOption = (raw: any): AiDisambiguationOption | null => {
  const employeeId = String(raw?.employeeId ?? raw?.employee_id ?? '').trim()
  const name = String(raw?.name ?? '').trim()
  const displayName = String(raw?.displayName ?? raw?.display_name ?? name).trim()
  if (!employeeId) return null
  return {
    employeeId,
    name: name || displayName,
    displayName: displayName || name || employeeId,
  }
}

const normalizeDisambiguation = (raw: any): AiDisambiguation | null => {
  const src = raw?.disambiguation ?? null
  if (!src) return null

  const matchedName = String(src?.matchedName ?? src?.matched_name ?? '').trim()
  const options = (Array.isArray(src?.options) ? src.options : [])
    .map(normalizeDisambiguationOption)
    .filter((o: AiDisambiguationOption | null): o is AiDisambiguationOption => Boolean(o))

  if (!matchedName || options.length === 0) return null
  return {matchedName, options}
}

const normalizeConfirmation = (raw: any): AiAllocationResponse['confirmation'] => {
  const src = raw?.confirmation ?? null
  if (!src) return null

  const pendingActions = (Array.isArray(src?.pendingActions) ? src.pendingActions : [])
    .map(normalizeAction)
    .filter((a: AiAllocationAction | null): a is AiAllocationAction => Boolean(a))

  if (pendingActions.length === 0) return null
  return {pendingActions}
}

const normalizeWarning = (raw: unknown): string => {
  if (raw === null || raw === undefined) return ''

  if (typeof raw === 'string') return raw.trim()

  if (typeof raw !== 'object') return String(raw).trim()

  const warning = raw as Record<string, unknown>
  const reason = String(warning.reason ?? warning.message ?? warning.warning ?? '').trim()
  if (!reason) return ''

  const action = warning.action
  if (action && typeof action === 'object') {
    const actionObj = action as Record<string, unknown>
    const className = String(actionObj.className ?? actionObj.class_name ?? '').trim()
    const subjectName = String(actionObj.subjectName ?? actionObj.subject_name ?? '').trim()
    if (className && subjectName) {
      return `${className} — ${subjectName}: ${reason}`
    }
  }

  return reason
}

/** Actions to preview/apply — uses confirmation.pendingActions when top-level actions are empty. */
export function getEffectiveAllocationActions(
  response: AiAllocationResponse
): AiAllocationAction[] {
  if (response.actions.length > 0) return response.actions
  return response.confirmation?.pendingActions ?? []
}

export function hasAllocationConfirmation(response: AiAllocationResponse): boolean {
  return (response.confirmation?.pendingActions?.length ?? 0) > 0
}

export function normalizeAllocationAiResponse(raw: unknown): AiAllocationResponse {
  const src = (raw as any)?.result ?? (raw as any)?.data ?? raw ?? {}
  const payload = src?.result ?? src

  const actions = (Array.isArray(payload?.actions) ? payload.actions : [])
    .map(normalizeAction)
    .filter((a: AiAllocationAction | null): a is AiAllocationAction => Boolean(a))

  const warnings = Array.isArray(payload?.warnings)
    ? payload.warnings.map(normalizeWarning).filter(Boolean)
    : []

  const usageSrc = payload?.usage ?? {}
  const usage = {
    inputTokens: Number(usageSrc?.inputTokens ?? 0) || 0,
    outputTokens: Number(usageSrc?.outputTokens ?? 0) || 0,
    totalTokens: Number(usageSrc?.totalTokens ?? 0) || 0,
  }

  return {
    previewMessage: String(payload?.previewMessage ?? payload?.preview_message ?? '').trim(),
    actions,
    warnings,
    usage,
    disambiguation: normalizeDisambiguation(payload),
    confirmation: normalizeConfirmation(payload),
  }
}

export function buildMockAllocationResponse(
  context: import('../types/teacherAllocationAi').AiAllocationContext,
  prompt: string,
  options?: {
    confirmOverwrite?: boolean
    pendingActions?: AiAllocationAction[]
  }
): AiAllocationResponse {
  if (options?.confirmOverwrite && options.pendingActions?.length) {
    return {
      previewMessage: `Applied ${options.pendingActions.length} teacher assignment(s).`,
      actions: options.pendingActions,
      warnings: [],
      usage: {inputTokens: 0, outputTokens: 0, totalTokens: 0},
      disambiguation: null,
      confirmation: null,
    }
  }

  const unassigned = context.subjects.filter((s) => !s.assignedTeacher)
  const teacherRefs = context.teachers.map((t) => t.employeeId)
  const actions: AiAllocationAction[] = []

  let idx = 0
  unassigned.forEach((subject) => {
    if (!teacherRefs.length) return
    const teacher = context.teachers[idx % teacherRefs.length]
    actions.push({
      type: 'assign',
      slotKey: subject.slotKey,
      teacherEmployeeId: teacher.employeeId,
      teacherName: teacher.name,
    })
    idx += 1
  })

  const balancePrompt = /balance|evenly|workload/i.test(prompt)
  const overwritePrompt = /overwrite|replace|reassign/i.test(prompt)
  const assignedSubjects = context.subjects.filter((s) => s.assignedTeacher)

  if (overwritePrompt && assignedSubjects.length > 0 && actions.length === 0) {
    const overwriteActions: AiAllocationAction[] = assignedSubjects
      .slice(0, 3)
      .map((subject, i) => {
        const teacher = context.teachers[i % context.teachers.length]
        return {
          type: 'assign',
          slotKey: subject.slotKey,
          teacherEmployeeId: teacher.employeeId,
          teacherName: teacher.name,
        }
      })

    return {
      previewMessage: `This will overwrite ${overwriteActions.length} existing assignment(s). Apply to confirm.`,
      actions: [],
      warnings: [],
      usage: {inputTokens: 0, outputTokens: 0, totalTokens: 0},
      disambiguation: null,
      confirmation: {pendingActions: overwriteActions},
    }
  }

  return {
    previewMessage:
      actions.length > 0
        ? balancePrompt
          ? `Proposed ${actions.length} balanced teacher assignment(s) across your teachers.`
          : `Proposed ${actions.length} teacher assignment(s) based on your request.`
        : 'No changes needed — all subjects in scope already have teachers assigned.',
    actions,
    warnings: [],
    usage: {inputTokens: 0, outputTokens: 0, totalTokens: 0},
    disambiguation: null,
    confirmation: null,
  }
}
